/**
 * Accessibility utility functions for the Volvo Trucks Web Configurator
 * Provides keyboard navigation, focus management, and ARIA helpers
 */

/**
 * Manages focus within a modal or dialog
 * Traps focus within the specified element and handles escape key
 */
export const trapFocus = (element: HTMLElement, onEscape?: () => void) => {
  const focusableElements = element.querySelectorAll(
    "button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
  ) as NodeListOf<HTMLElement>;
  
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && onEscape) {
      onEscape();
      return;
    }

    if (event.key === "Tab") {
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    }
  };

  element.addEventListener("keydown", handleKeyDown);
  
  // Focus the first element
  if (firstElement) {
    firstElement.focus();
  }

  // Return cleanup function
  return () => {
    element.removeEventListener("keydown", handleKeyDown);
  };
};

/**
 * Announces a message to screen readers
 */
export const announceToScreenReader = (message: string, priority: "polite" | "assertive" = "polite") => {
  const announcement = document.createElement("div");
  announcement.setAttribute("aria-live", priority);
  announcement.setAttribute("aria-atomic", "true");
  announcement.className = "sr-only";
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Generates a unique ID for accessibility attributes
 */
export const generateAccessibleId = (prefix: string, suffix?: string): string => {
  const randomId = Math.random().toString(36).substr(2, 9);
  return suffix ? `${prefix}-${suffix}-${randomId}` : `${prefix}-${randomId}`;
};


/**
 * Handles keyboard navigation for custom components
 */
export const handleKeyboardNavigation = (
  event: React.KeyboardEvent,
  onActivate?: () => void,
  onNext?: () => void,
  onPrevious?: () => void,
  onEscape?: () => void
) => {
  switch (event.key) {
  case "Enter":
  case " ":
    event.preventDefault();
    onActivate?.();
    break;
  case "ArrowRight":
  case "ArrowDown":
    event.preventDefault();
    onNext?.();
    break;
  case "ArrowLeft":
  case "ArrowUp":
    event.preventDefault();
    onPrevious?.();
    break;
  case "Escape":
    event.preventDefault();
    onEscape?.();
    break;
  case "Home":
    event.preventDefault();
    // Focus first item
    break;
  case "End":
    event.preventDefault();
    // Focus last item
    break;
  }
};

/**
 * Manages focus restoration
 */
export const createFocusManager = () => {
  let previouslyFocusedElement: HTMLElement | null = null;

  const saveFocus = () => {
    previouslyFocusedElement = document.activeElement as HTMLElement;
  };

  const restoreFocus = () => {
    if (previouslyFocusedElement && 
        previouslyFocusedElement.offsetParent !== null && 
        !previouslyFocusedElement.hasAttribute("aria-hidden")) {
      previouslyFocusedElement.focus();
    }
  };

  return { saveFocus, restoreFocus };
};
